import { yupResolver } from "@hookform/resolvers/yup";
import { useGetCondominioPossuiContaProvisionamentoAtiva } from "hooks/condominio/useGetCondominioPossuiContaProvisionamentoAtiva";
import { usePostValidarDespesaDuplicadaNumeroDocumentoFornecedor } from "hooks/contasPagar/usePostValidarDespesaDuplicadaGroupPay";
import { usePostCadastrarParceladaUnicaGroupPay } from "hooks/groupPay/usePostCadastrarDespesaParceladaGroupPay ";
import { useContext, useEffect } from "react";
import { Resolver, useFieldArray, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { CadastrarDespesaParceladaGroupPaySchema } from "shared/yup/schemas/CadastrarDespesaParceladaGroupPaySchema";
import showAlert from "shared/util/functions/alert/showAlert";
import {
  criarDataByLocalDate,
  formatarValorMonetarioInputNumerico,
  valoresDiferentes,
  getMesCompetenciaParaVisualizacao,
  limparCpfCnpj,
} from "shared/util/functions/formatacoesDeValores/FormatarValores";
import { queryClient } from "shared/util/queryClient";
import { ClassePlanoContasYupSchemaDTO } from "types/dto/contasPagar/ClassePlanoContasYupSchemaDTO";
import { RetencoesYupSchemaDTO } from "types/dto/imposto/RetencoesYupSchemaDTO";
import { CadastroClasseContaContasPagar } from "types/request/contasPagar/CadastroClasseContaContasPagar";
import { ValidarDespesaDuplicadaNumeroDocumentoFornecedorRequest } from "types/request/contasPagar/ValidarDespesaDuplicadaNumeroDocumentoFornecedorRequest";
import { CadastroRetencoesContasPagarGroupPayRequest } from "types/request/contasPagar/imposto/CadastroRetencoesContasPagarGroupPayRequest";
import { CadastroContasPagarParceladaGroupPayRequest } from "types/request/groupPay/CadastroContasPagarParceladaGroupPayRequest";
import { DespesaGroupPayDetalhada } from "types/response/groupPay/DespesaGroupPayDetalhadaResponse";
import { ValoresDespesaParceladaResponse } from "types/request/contasPagar/ValoresDespesaParceladaResponse";
import { CadastrarDespesaParceladaGroupPayForm } from "types/reactHookForm/formTypes/CadastrarDespesaParceladaGroupPayForm";
import { AnexoItem } from "shared/components/input/InputMultiAnexos";
import { AnexoType } from "types/anexo/AnexoType";
import { useGetContaPrincipalByCondominio } from "hooks/conta/useGetContaPrincipalByCondominio";
import { usePostVerificarImpactoNaReinf } from "hooks/reinf/usePostVerificarImpactoNaReinf";
import { TipoSerieReinf } from "constants/EnumTipoSerieReinf";
import { AlertEdicaoReifnDTO } from "types/dto/reinf/AlertEdicaoReifnDTO";
import { ContasPagarDTO } from "types/dto/contasPagar/ContasPagarDTO";
import { VerificarImpactoReinfRequest } from "types/request/reinf/VerificarImpactoReinfRequest";
import { UserContext } from "context/UserContext";

interface UseCadastrarDespesaParceladaGroupPayProps {
  valorTotalParcelas: number;
  showModal: boolean;
  despesaGroupPay: DespesaGroupPayDetalhada;
  parcelas: ValoresDespesaParceladaResponse[];
  onCloseModal: () => void;
  condominioId: number;
  idsDespesasAgrupadas?: number[];
}

const DATA_IMAGE_PNG_BASE64 = "data:image/png;base64,";

export const useCadastrarDespesaParceladaGroupPay = (props: UseCadastrarDespesaParceladaGroupPayProps) => {
  const { data: responseCondominioPossuiContaProvisionamento } = useGetCondominioPossuiContaProvisionamentoAtiva(
    props.condominioId
  );
  const { data: responseContaPrincipal } = useGetContaPrincipalByCondominio(props.condominioId);

  const { mutateAsync: cadastrarDespesaParceladaGroupPay } = usePostCadastrarParceladaUnicaGroupPay();
  const { mutateAsync: validarDuplicidadeDespesa } = usePostValidarDespesaDuplicadaNumeroDocumentoFornecedor();
  const { mutateAsync: validarImpactoReinf } = usePostVerificarImpactoNaReinf();

  const { competenciaSelecionada } = useContext(UserContext);

  const montarRetencoesDefault = (): RetencoesYupSchemaDTO[] => {
    let retencoesDefault = [] as RetencoesYupSchemaDTO[];
    if (
      props.despesaGroupPay?.sugestoesDeImpostosParaCadastro &&
      props.despesaGroupPay?.sugestoesDeImpostosParaCadastro.length > 0
    ) {
      retencoesDefault = props.despesaGroupPay.sugestoesDeImpostosParaCadastro.map((sugestao) => ({
        imposto: {
          label: sugestao.descricaoImposto,
          value: { impostoId: sugestao.impostoId, aliquota: sugestao.aliquota, tipoImposto: sugestao.tipoImposto },
        },
        valor: sugestao.valorImposto,
        dataVencimento: props.despesaGroupPay.dataVencimento,
        mesCompetencia: competenciaSelecionada,
        valorBase: props.despesaGroupPay.valor,
      }));
    } else {
      retencoesDefault = [
        {
          imposto: null,
          valorBase: props.despesaGroupPay.valor,
          valor: undefined,
          mesCompetencia: null,
          dataVencimento: null,
          contribuinte: null,
          conta: null,
          formaPagamento: null,
          codigoDeBarras: "",
          anexo: null,
        },
      ];
    }
    return retencoesDefault;
  };

  const yupResolverInstance = yupResolver<CadastrarDespesaParceladaGroupPayForm>(CadastrarDespesaParceladaGroupPaySchema);
  const customResolver: Resolver<CadastrarDespesaParceladaGroupPayForm> = async (values, context, options) => {
    const result = await yupResolverInstance(values, context, options);
    const errors = result.errors as Record<string, unknown> | undefined;
    const retencoesError = errors?.retencoes as { message?: string; type?: string; [key: string]: unknown } | undefined;
    const hasRetencoesArrayError = retencoesError && typeof retencoesError.message === "string";

    if (hasRetencoesArrayError && values.retencoes?.length) {
      const newRetencoesError = { ...retencoesError };
      values.retencoes.forEach((_: RetencoesYupSchemaDTO, index: number) => {
        (newRetencoesError as Record<string, unknown>)[String(index)] = {
          ...((newRetencoesError as Record<string, unknown>)[String(index)] as object),
          valor: { type: "custom" as const, message: "" },
        };
      });
      result.errors = { ...result.errors, retencoes: newRetencoesError };
    }
    return result;
  };

  const form = useForm<CadastrarDespesaParceladaGroupPayForm>({
    resolver: customResolver,
    defaultValues: {
      condominio: { label: props.despesaGroupPay.nomeCondominio || "", value: props.despesaGroupPay.idCondominio },
      valorTotal: props.valorTotalParcelas,
      dataEmissao: criarDataByLocalDate(String(props.despesaGroupPay.dataEmissao)) ?? null,
      fornecedor: props.despesaGroupPay.idFornecedorEmissor
        ? {
            label: props.despesaGroupPay.nomeEmissor,
            value: { fornecedorId: props.despesaGroupPay.idFornecedorEmissor },
          }
        : {
            label: props.despesaGroupPay.nomeEmissor || "",
            value: { nomeFornecedor: props.despesaGroupPay.nomeEmissor, fornecedorId: null },
          },
      numeroDocumento: props.despesaGroupPay.numeroDocumento || null,
      serieNf: props.despesaGroupPay.serieNf || null,
      parcelas: props.parcelas,
      classesConta: [
        {
          classePlano: {
            label: props.despesaGroupPay.classePlanoContaVinculoFornecedorResponse?.classePlanoContaNome || "",
            value: props.despesaGroupPay.classePlanoContaVinculoFornecedorResponse
              ? {
                  idClassePlano: props.despesaGroupPay.classePlanoContaVinculoFornecedorResponse.classePlanoContaId,
                  idContaProvisionamentoVinculada:
                    props.despesaGroupPay.classePlanoContaVinculoFornecedorResponse.idContaProvisionamentoVinculada,
                  nomeContaProvisionamentoVinculada:
                    props.despesaGroupPay.classePlanoContaVinculoFornecedorResponse.nomeContaProvisionamentoVinculada,
                }
              : {},
          },
          contaProvisionamento: props.despesaGroupPay.classePlanoContaVinculoFornecedorResponse
            ?.idContaProvisionamentoVinculada
            ? {
                label:
                  props.despesaGroupPay.classePlanoContaVinculoFornecedorResponse.nomeContaProvisionamentoVinculada ||
                  "",
                value:
                  props.despesaGroupPay.classePlanoContaVinculoFornecedorResponse.idContaProvisionamentoVinculada || "",
              }
            : null,
          valorBruto: props.valorTotalParcelas,
          descricao: props.despesaGroupPay.classePlanoContaVinculoFornecedorResponse?.descricao || "",
          condominioPossuiContaProvisionamentoAtiva: responseCondominioPossuiContaProvisionamento?.payload,
        },
      ],
      naturezaRendimento: {
        label: props.despesaGroupPay?.naturezaRendimentoDTO?.codigoDescricao,
        value: props.despesaGroupPay?.naturezaRendimentoDTO?.id,
      },
    },
  });

  const { control, handleSubmit, setValue, reset, watch, formState } = form;

  const retencoes = watch("retencoes", []);
  const classesConta = watch("classesConta", []);
  const parcelas = watch("parcelas", []);
  const reterEmTodasParcelas = watch("reterEmTodasParcelas");
  const valorTotalInput = watch("valorTotal");
  const juros = watch("juros") || 0;
  const multa = watch("multa") || 0;
  const desconto = watch("desconto") || 0;

  const valorTotalBruto =
    typeof valorTotalInput === "number"
      ? valorTotalInput
      : formatarValorMonetarioInputNumerico(String(valorTotalInput));

  const valorTotalImpostos = retencoes.reduce((total, retencao) => {
    const valor = retencao.valor || 0;
    const valorNumerico = typeof valor === "number" ? valor : formatarValorMonetarioInputNumerico(String(valor));
    return total + (isNaN(valorNumerico) ? 0 : valorNumerico);
  }, 0);

  const valorTotalBrutoClassesConta = classesConta.reduce((total, classe) => {
    const valor = classe.valorBruto || 0;
    const valorNumerico = typeof valor === "number" ? valor : formatarValorMonetarioInputNumerico(String(valor));
    return total + (isNaN(valorNumerico) ? 0 : valorNumerico);
  }, 0);

  const valorTotalParcelado = parcelas.reduce((total, parcela) => {
    const valor = parcela.valorParcela || 0;
    const valorNumerico = typeof valor === "number" ? valor : formatarValorMonetarioInputNumerico(String(valor));
    return total + (isNaN(valorNumerico) ? 0 : valorNumerico);
  }, 0);

  const somatorioValorLiquidoParcelas = parcelas.reduce((total, parcela) => {
    const valor = parcela.valorLiquido || 0;
    const valorNumerico = typeof valor === "number" ? valor : formatarValorMonetarioInputNumerico(String(valor));
    return total + (isNaN(valorNumerico) ? 0 : valorNumerico);
  }, 0);

  const valorLiquidoTotalComImpostos = somatorioValorLiquidoParcelas + valorTotalImpostos;

  const {
    fields: fieldsClassesConta,
    append: adicionarClasseConta,
    remove: removerClasseConta,
  } = useFieldArray({ control, name: "classesConta" });

  const {
    fields: fieldsParcelas,
    append: adicionarParcela,
    remove: removerParcela,
  } = useFieldArray({ control, name: "parcelas" });

  const {
    fields: fieldsRetencoes,
    append: adicionarRetencao,
    remove: removerRetencao,
  } = useFieldArray({ control, name: "retencoes" });

  useEffect(() => {
    const parcelasAtuais = watch("parcelas") || [];
    if (parcelasAtuais.length === 0) return;

    if (reterEmTodasParcelas) {
      const valorLiquidoTotal = valorTotalBruto - valorTotalImpostos;
      const valorLiquidoPorParcela = parseFloat((valorLiquidoTotal / parcelasAtuais.length).toFixed(2));
      const diferenca = parseFloat((valorLiquidoTotal - valorLiquidoPorParcela * parcelasAtuais.length).toFixed(2));

      parcelasAtuais.forEach((_, index) => {
        const valorFinal = index === 0 ? valorLiquidoPorParcela + diferenca : valorLiquidoPorParcela;
        if (watch(`parcelas.${index}.valorLiquido`) !== valorFinal) {
          setValue(`parcelas.${index}.valorLiquido`, valorFinal, { shouldValidate: true });
        }
      });
    } else {
      parcelasAtuais.forEach((parcela, index) => {
        const valorParcelado = parcela.valorParcela ?? 0;
        const valorLiquidoFinal = index === 0 ? valorParcelado - valorTotalImpostos : valorParcelado;

        if (watch(`parcelas.${index}.valorLiquido`) !== valorLiquidoFinal) {
          setValue(`parcelas.${index}.valorLiquido`, valorLiquidoFinal, { shouldValidate: true });
        }
      });
    }

    const primeiraParcela = watch("parcelas.0");
    if (!primeiraParcela) return;

    const valorLiquidoPrimeiraParcela = primeiraParcela.valorLiquido ?? 0;

    const jurosNumerico = formatarValorMonetarioInputNumerico(String(juros)) ?? 0;
    const multaNumerico = formatarValorMonetarioInputNumerico(String(multa)) ?? 0;
    const descontoNumerico = formatarValorMonetarioInputNumerico(String(desconto)) ?? 0;

    const valorPagoCalculado = valorLiquidoPrimeiraParcela + jurosNumerico + multaNumerico - descontoNumerico;
    const valorFinal = valorPagoCalculado > 0 ? valorPagoCalculado : 0;

    if (watch("valorPago") !== valorFinal) {
      setValue("valorPago", valorFinal);
    }
  }, [reterEmTodasParcelas, valorTotalBruto, valorTotalImpostos, parcelas, juros, multa, desconto, setValue, watch]);

  useEffect(() => {
    if (props.showModal) {
      const retencoesDefault = montarRetencoesDefault();
      if (retencoesDefault.length > 0) {
        setValue("retencoes", retencoesDefault);
      }
    }
  }, [props.showModal, setValue]);

  useEffect(() => {
    if (responseContaPrincipal?.payload?.contaBancaria) {
      parcelas.forEach((_, index) => {
        setValue(
          `parcelas.${index}.conta`,
          {
            label: responseContaPrincipal.payload.descricao,
            value: responseContaPrincipal.payload,
          },
          { shouldValidate: true }
        );
      });
    }
  }, [responseContaPrincipal, parcelas, setValue]);

  useEffect(() => {
    classesConta.forEach((_, index) => {
      setValue(
        `classesConta.${index}.condominioPossuiContaProvisionamentoAtiva`,
        responseCondominioPossuiContaProvisionamento?.payload,
        {
          shouldValidate: true,
        }
      );
    });
  }, [responseCondominioPossuiContaProvisionamento, classesConta, setValue]);

  const cadastrarDespesa = async (request: CadastroContasPagarParceladaGroupPayRequest) => {
    try {
      await cadastrarDespesaParceladaGroupPay(request);
      queryClient.invalidateQueries(["despesas-paginadas-group-pay"]);
      fecharModal();
    } catch (err) {}
  };

  const montarImpostosParaValidacaoReinf = (retencoesForm: RetencoesYupSchemaDTO[] = []) => {
    return retencoesForm
      .filter((r) => !!r?.imposto?.value?.impostoId)
      .map((retencao) => ({
        mesCompetencia: retencao.mesCompetencia?.toISOString(),
        impostoId: retencao.imposto!.value.impostoId,
      })) as ContasPagarDTO[];
  };

  const getImpactosDaReinfSerie2000 = async (formValues: CadastrarDespesaParceladaGroupPayForm) => {
    let alerta: AlertEdicaoReifnDTO = {} as AlertEdicaoReifnDTO;
    const primeiraParcela = formValues.parcelas?.[0];
    if (!primeiraParcela?.mesCompetencia) return alerta;

    const mesCompetencia = new Date(primeiraParcela.mesCompetencia as Date);
    mesCompetencia.setDate(1);

    const contasPagarDTO = {
      mesCompetencia: mesCompetencia.toISOString(),
      impostos: montarImpostosParaValidacaoReinf(formValues.retencoes || []),
    } as ContasPagarDTO;

    const requestReinf = {
      contasPagarDTO,
      tipoSerieReinf: TipoSerieReinf.SERIE_2000,
    } as VerificarImpactoReinfRequest;

    try {
      const response: any = await validarImpactoReinf(requestReinf);
      alerta = (response?.data ?? response) as AlertEdicaoReifnDTO;
    } catch (err) {
      toast.error("Erro ao verificar impacto na REINF");
    }
    return alerta;
  };

  const getImpactosDaReinfSerie4000 = async (formValues: CadastrarDespesaParceladaGroupPayForm) => {
    let alerta: AlertEdicaoReifnDTO = {} as AlertEdicaoReifnDTO;
    const primeiraParcela = formValues.parcelas?.[0];
    if (!primeiraParcela?.mesCompetencia) return alerta;

    const mesCompetencia = new Date(primeiraParcela.mesCompetencia as Date);
    mesCompetencia.setDate(1);

    const contasPagarDTO = {
      mesCompetencia: mesCompetencia.toISOString(),
      impostos: montarImpostosParaValidacaoReinf(formValues.retencoes || []),
    } as ContasPagarDTO;

    const requestReinf = {
      contasPagarDTO,
      tipoSerieReinf: TipoSerieReinf.SERIE_4000,
    } as VerificarImpactoReinfRequest;

    try {
      const response: any = await validarImpactoReinf(requestReinf);
      alerta = (response?.data ?? response) as AlertEdicaoReifnDTO;
    } catch (err) {
      toast.error("Erro ao verificar impacto na REINF");
    }
    return alerta;
  };

  const deveEmitirAvisoReinf = (validacoes: AlertEdicaoReifnDTO) => {
    return (validacoes as any)?.existeEnviadaMesCompetencia || (validacoes as any)?.existeEnviadaMesCompetenciaOriginal;
  };

  const deveExibirAlertaBloqueioDeCadastroDeDespesaReinf = (validacoes: AlertEdicaoReifnDTO) => {
    return (
      (validacoes as any)?.existeEmProcessamentoMesCompetencia ||
      (validacoes as any)?.existeEmProcessamentoMesCompetenciaOriginal
    );
  };

  const getTextoAlertaReinf = (alertaReinf: AlertEdicaoReifnDTO, mesCompetencia: Date) => {
    const mesCompetenciaFormatado = getMesCompetenciaParaVisualizacao(mesCompetencia);
    let mensagemDeAlerta = "";
    if (
      (alertaReinf as any)?.existeEmProcessamentoMesCompetencia ||
      (alertaReinf as any)?.existeEmProcessamentoMesCompetenciaOriginal
    ) {
      mensagemDeAlerta = "Não é possivel cadastrar a despesa. Aguardando retorno de processamento da EFD-Reinf.";
    } else if (
      (alertaReinf as any)?.existeEnviadaMesCompetencia ||
      (alertaReinf as any)?.existeEnviadaMesCompetenciaOriginal
    ) {
      mensagemDeAlerta = `A EFD-Reinf já foi enviada na competência ${mesCompetenciaFormatado}. Ao cadastrar uma nova despesa, será necessário realizar um novo envio. Lembrando que qualquer informação incorreta ou envio fora do prazo, o condomínio estará sujeito a multa.`;
    }
    return mensagemDeAlerta;
  };

  const mapearAnexosParaRequest = (anexos: AnexoItem[], isRetencao: boolean = false): AnexoType[] => {
    if (!anexos) return [];

    return anexos?.map((anexo) => ({
      nome: anexo.nome,
      base64: DATA_IMAGE_PNG_BASE64 + anexo.arquivo || "",
      anexoComprovante: isRetencao ? true : false,
    }));
  };

  const onSubmit = async (formValues: CadastrarDespesaParceladaGroupPayForm) => {
    if (valoresDiferentes(valorTotalParcelado, valorTotalBruto)) {
      toast.error("O somatório dos valores informados para as parcelas deve ser igual ao valor total.");
      return;
    }
    if (valoresDiferentes(valorTotalBrutoClassesConta, valorTotalBruto)) {
      toast.error("O somatório dos valores informados para as classes de contas deve ser igual ao valor total.");
      return;
    }

    if (valoresDiferentes(valorTotalBruto, valorLiquidoTotalComImpostos)) {
      toast.error("O somatório dos valores das parcelas e a retenção deve ser igual ao valor total bruto.");
      return;
    }

    const deveCriarFornecedor = !formValues?.fornecedor?.value?.fornecedorId && !!props.despesaGroupPay?.nomeEmissor;
    const request: CadastroContasPagarParceladaGroupPayRequest = {
      valorBruto: valorTotalBruto,
      valorLiquido: valorTotalBruto - valorTotalImpostos,
      valorPago: formValues.valorPago,
      reterEmTodasAsParcelas: formValues.reterEmTodasParcelas || false,
      fornecedorId: formValues.fornecedor.value.fornecedorId,
      nomeFornecedor: deveCriarFornecedor ? props?.despesaGroupPay?.nomeEmissor ?? null : null,
      cpfCnpjFornecedor:
        deveCriarFornecedor && props?.despesaGroupPay?.cpfCnpjEmissor
          ? limparCpfCnpj(props.despesaGroupPay.cpfCnpjEmissor)
          : null,
      favorecidoFornecedorId: formValues.isFavorecidoFornecedor ? formValues.favorecido?.value.fornecedorId : undefined,

      naturezaRendimentoId: formValues.naturezaRendimento?.value,
      condominioId: props?.condominioId,
      parcelas: formValues.parcelas.map((parcela, index) => montarCadastroParcelaContasPagar(parcela, index)),
      contaId: formValues.parcelas[0].conta?.value?.id,
      classesConta: formValues.classesConta.map((classeConta) => montarCadastroClasseContaContasPagar(classeConta)),
      retencoes: formValues.retencoes
        .filter((retencao) => retencao.imposto)
        .map((retencao) => montarCadastroRetencaoContasPagar(retencao)),
      anexos: formValues.anexos && formValues.anexos.length > 0 ? mapearAnexosParaRequest(formValues.anexos, false) : null,
      incluirAcrescimosDescontos: formValues.incluirAcrescimosDescontos || false,
      juros: formValues.juros ? formatarValorMonetarioInputNumerico(String(formValues.juros)) : null,
      multa: formValues.multa ? formatarValorMonetarioInputNumerico(String(formValues.multa)) : null,
      desconto: formValues.desconto ? formatarValorMonetarioInputNumerico(String(formValues.desconto)) : null,
      dataPagamento: formValues.dataPagamento?.toISOString() ?? null,
      dataLiquidacao: formValues.dataLiquidacao?.toISOString() ?? null,
      numeroDocumento: formValues.numeroDocumento?.trim() || null,
      serieNf: formValues.serieNf?.trim() || null,
      dataEmissao: formValues.dataEmissao?.toISOString() ?? null,
      idDespesaGroupPay: props.despesaGroupPay.id,
      cheque: formValues.parcelas?.[0]?.numeroCheque ? { numero: formValues.parcelas?.[0]?.numeroCheque } : null,
    };

    const validacao: ValidarDespesaDuplicadaNumeroDocumentoFornecedorRequest = {
      fornecedorId: request.fornecedorId,
      numeroDocumento: props.despesaGroupPay.numeroDocumento || "",
      condominioId: props?.condominioId,
    };

    let validacoesDaReinfSerie2000 = {} as AlertEdicaoReifnDTO;
    let validacoesDaReinfSerie4000 = {} as AlertEdicaoReifnDTO;

    if (formValues.retencoes.filter((r) => r.imposto != null).length > 0) {
      validacoesDaReinfSerie2000 = await getImpactosDaReinfSerie2000(formValues);
      validacoesDaReinfSerie4000 = await getImpactosDaReinfSerie4000(formValues);
    }

    if (
      deveExibirAlertaBloqueioDeCadastroDeDespesaReinf(validacoesDaReinfSerie2000) &&
      formValues.parcelas?.[0]?.mesCompetencia
    ) {
      showAlert({
        texto: getTextoAlertaReinf(validacoesDaReinfSerie2000, formValues.parcelas[0].mesCompetencia as Date),
        tipoAlerta: "OK_ALERT",
      });
      return;
    } else if (
      deveExibirAlertaBloqueioDeCadastroDeDespesaReinf(validacoesDaReinfSerie4000) &&
      formValues.parcelas?.[0]?.mesCompetencia
    ) {
      showAlert({
        texto: getTextoAlertaReinf(validacoesDaReinfSerie4000, formValues.parcelas[0].mesCompetencia as Date),
        tipoAlerta: "OK_ALERT",
      });
      return;
    }

    const continuarFluxoCadastro = async () => {
      try {
        const res: any = await validarDuplicidadeDespesa(validacao);
        if (res?.data) {
          showAlert({
            texto: "Já existe uma despesa com o mesmo número de documento e fornecedor. Deseja continuar?",
            tipoAlerta: "CONFIRMAR_CANCELAR_ALERT",
            labelBotaoCancelar: "Não",
            labelBotaoConfirmar: "Sim",
            onConfirm: () => {
              cadastrarDespesa(request).catch(() => {});
            },
          });
        } else {
          await cadastrarDespesa(request);
        }
      } catch (err: any) {
        if (
          err?.response?.data?.validation?.[0]?.groupCode === "contasPagar.error.duplicidadeNumeroDocumentoFornecedor"
        ) {
          showAlert({
            texto: "Já existe uma despesa com o mesmo número de documento e fornecedor. Deseja continuar?",
            tipoAlerta: "CONFIRMAR_CANCELAR_ALERT",
            labelBotaoCancelar: "Não",
            labelBotaoConfirmar: "Sim",
            onConfirm: () => {
              cadastrarDespesa(request).catch(() => {});
            },
          });
        } else {
          throw err;
        }
      }
    };

    if (deveEmitirAvisoReinf(validacoesDaReinfSerie2000) && formValues.parcelas?.[0]?.mesCompetencia) {
      showAlert({
        texto: getTextoAlertaReinf(validacoesDaReinfSerie2000, formValues.parcelas[0].mesCompetencia as Date),
        tipoAlerta: "CONFIRMAR_CANCELAR_ALERT",
        labelBotaoCancelar: "Cancelar",
        labelBotaoConfirmar: "OK",
        onConfirm: continuarFluxoCadastro,
      });
      return;
    } else if (deveEmitirAvisoReinf(validacoesDaReinfSerie4000) && formValues.parcelas?.[0]?.mesCompetencia) {
      showAlert({
        texto: getTextoAlertaReinf(validacoesDaReinfSerie4000, formValues.parcelas[0].mesCompetencia as Date),
        tipoAlerta: "CONFIRMAR_CANCELAR_ALERT",
        labelBotaoCancelar: "Cancelar",
        labelBotaoConfirmar: "OK",
        onConfirm: continuarFluxoCadastro,
      });
      return;
    }

    await continuarFluxoCadastro();
  };

  const montarCadastroParcelaContasPagar = (parcela: ValoresDespesaParceladaResponse, index: number) => {
    const idDespesaGroupPay = props.idsDespesasAgrupadas && props.idsDespesasAgrupadas.length > 0
      ? props.idsDespesasAgrupadas[index]
      : props.despesaGroupPay.id;

    return {
      mesCompetencia: parcela?.mesCompetencia!.toISOString(),
      dataVencimento: parcela?.dataVencimento!.toISOString(),
      valorParcelado: parcela.valorParcela!,
      valorLiquido: parcela.valorLiquido!,
      idDespesaGroupPay: idDespesaGroupPay,
      contaId: parcela.conta?.value?.id,
      formaPagamento: parcela.formaPagamento?.value,
      codigoDeBarras: parcela.codigoDeBarras,
      anexos: parcela.anexos && parcela.anexos.length > 0 ? mapearAnexosParaRequest(parcela.anexos) : null,
      tipoFormaPagamentoPix: parcela.tipoFormaPagamentoPix,
      codigoCopiaColaPix: parcela.codigoCopiaColaPix,
      cheque: parcela?.numeroCheque ? { numero: parcela.numeroCheque } : null,
    };
  };

  const montarCadastroClasseContaContasPagar = (classeConta: ClassePlanoContasYupSchemaDTO) => {
    let cadastroClasseConta: CadastroClasseContaContasPagar = {
      classeContaId: classeConta?.classePlano?.value.idClassePlano,
      valor: classeConta.valorBruto,
      descricao: classeConta.descricao,
    };
    if (responseCondominioPossuiContaProvisionamento?.payload) {
      cadastroClasseConta.contaProvisionamentoId = classeConta?.contaProvisionamento?.value;
    }
    return cadastroClasseConta;
  };

  const montarCadastroRetencaoContasPagar = (
    retencao: RetencoesYupSchemaDTO
  ): CadastroRetencoesContasPagarGroupPayRequest => {
    return {
      valorBase: retencao.valorBase ?? null,
      impostoId: retencao?.imposto?.value.impostoId ?? null,
      mesCompetencia: retencao.mesCompetencia?.toISOString() ?? null,
      dataVencimento: retencao.dataVencimento?.toISOString() ?? null,
      valor: retencao.valor ?? null,
      contribuinte: retencao.contribuinte?.value ?? null,
      contaId: retencao.conta?.value ? Number(retencao.conta.value.id) : null,
      tipoFormaPagamento: retencao.formaPagamento?.value ?? null,
      codigoDeBarras: retencao.codigoDeBarras ?? null,
      tipoFormaPagamentoPix: retencao.tipoFormaPagamentoPix ?? null,
      codigoCopiaColaPix: retencao.codigoCopiaColaPix ?? null,
      anexo: retencao.anexo ? mapearAnexosParaRequest(retencao?.anexo, true) : null,
    };
  };

  const fecharModal = () => {
    reset();
    props.onCloseModal();
  };

  const handleCloseModal = () => {
    if (formState.isDirty) {
      showAlert({
        texto: "Existem alterações que não foram salvas na tela. Tem certeza que deseja sair sem salvar?",
        tipoAlerta: "CONFIRMAR_CANCELAR_ALERT",
        onConfirm: fecharModal,
      });
    } else {
      fecharModal();
    }
  };

  return {
    form,
    onSubmit: handleSubmit(onSubmit),
    handleCloseModal,
    fieldsClassesConta,
    adicionarClasseConta,
    removerClasseConta,
    fieldsParcelas,
    adicionarParcela,
    removerParcela,
    fieldsRetencoes,
    adicionarRetencao,
    removerRetencao,
    valorTotalBruto,
    valorTotalImpostos,
    valorTotalBrutoClassesConta,
    valorTotalParcelado,
    somatorioValorLiquidoParcelas,
    valorLiquidoTotalComImpostos,
    responseCondominioPossuiContaProvisionamento,
  };
};
